/* eslint-disable no-empty-pattern */
/* eslint-disable import/no-cycle */
/* eslint-disable no-shadow */
import {
  GetterTree, MutationTree, ActionTree, Module
} from 'vuex';
import IVehicleSearchState from '@/store/states/interfaces/vehicle-search-state.d';

import { vehicleSearchService } from '@/services/vehicle-search-service';
import Derivative from '@/models/vehicle/derivative';
import IManufacturer from '@/models/vehicle/interfaces/manufacturer.d';
import IRange from '@/models/vehicle/interfaces/range.d';
import Results from '@/models/vehicle/results';
import VehicleModuleOptions from '@/models/VehicleModuleOptions';
import VehicleStatusType from '@/enums/VehicleStatusType';
import Colour from '@/models/vehicle/colour';
import VehicleSearchState from '../states/vehicle-search-state';
import IRootState from '../states/interfaces/index.d';

export const state: IVehicleSearchState = new VehicleSearchState();

export const getters: GetterTree<IVehicleSearchState, IRootState> = {
  manufacturers: (state) => state.manufacturers,
  ranges: (state) => state.ranges,
  derivatives: (state) => state.filteredResults,
  models: (state) => state.results.models,
  fuelTypes: (state) => state.filteredResults
    .map((v) => v.fuelTypeName)
    .filter((item, i, ar) => ar.indexOf(item) === i),
  transmissions: (state) => state.filteredResults
    .map((v) => v.transmissionName)
    .filter((item, i, ar) => ar.indexOf(item) === i),
  doors: (state) => state.filteredResults
    .map((v) => v.doors)
    .filter((item, i, ar) => ar.indexOf(item) === i),
  trims: (state) => state.filteredResults
    .map((v) => v.trimName)
    .filter((item, i, ar) => ar.indexOf(item) === i && item !== '')
    .sort(),
  engineSizes: (state) => state.filteredResults
    .map((v) => v.engineSize)
    .filter((item, i, ar) => ar.indexOf(item) === i)
    .sort(),
  bodystyles: (state) => state.results.bodystyles,
  disableManufacturerInput: (state) => state.manufacturers === null
    || state.manufacturers.length === 0,
  disableRangeInput: (state) => state.ranges === null
    || state.ranges.length === 0,
  colours: (state) => {
    const colours = state.filteredResults.flatMap((d) => d.colours);

    if (colours) {
      const sortedColours: Record<string, string[]> = colours.reduce((r, a: Colour) => {
        r[a.basicColour] = r[a.basicColour] || new Set();
        r[a.basicColour].add(a.name);
        return r;
      }, Object.create(null));

      return sortedColours;
    }

    return null;
  }
};

export const mutations: MutationTree<IVehicleSearchState> = {
  setManufacturers: (state, manufacturers: IManufacturer[] | null) => {
    state.manufacturers = manufacturers;
  },
  setRanges: (state, ranges: IRange[] | null) => {
    state.ranges = ranges;
  },
  setResults: (state, results: Results) => {
    state.results = results;
  },
  setFilteredResults: (state, filteredResults: Derivative[]) => {
    state.filteredResults = filteredResults;
  },
  setBodystyles: (state, bodystyles: string[]) => {
    state.results.bodystyles = bodystyles;
  }
};

export const actions: ActionTree<IVehicleSearchState, IRootState> = {
  async updateManufacturers({
    state, commit, dispatch, rootState
  }) {
    const hasInitManufacturer = rootState.initialSettingsModule.initialSettings.manufacturers !== null && rootState.initialSettingsModule.initialSettings.manufacturers.length > 0;
    const hasAllowedManufacturers = rootState.initialSettingsModule.initialSettings.allowedManufacturerIds !== null && rootState.initialSettingsModule.initialSettings.allowedManufacturerIds.length > 0;

    if (hasInitManufacturer && hasAllowedManufacturers) {
      const initialManufacturerId = rootState.initialSettingsModule.initialSettings.manufacturers![0];
      const included = rootState.initialSettingsModule.initialSettings.allowedManufacturerIds.includes(initialManufacturerId);

      if (!included) {
        rootState.initialSettingsModule.initialSettings.allowedManufacturerIds.push(initialManufacturerId);
      }
    }

    if (hasAllowedManufacturers) {
      dispatch('searchQueryModule/submitManufacturerIds', rootState.initialSettingsModule.initialSettings.allowedManufacturerIds, { root: true });
    }

    const manufacturers = await vehicleSearchService.fetchManufacturers(rootState[`searchQueryModule${state.moduleKey}`].searchQuery);
    commit('setManufacturers', manufacturers);

    let validInitManufacturer = false;

    if (state.vehicleStatusType === VehicleStatusType.Initial && hasInitManufacturer) {
      const initialManufacturer = manufacturers.find((x) => x.manufacturerId === rootState.initialSettingsModule.initialSettings.manufacturers![0]);

      if (initialManufacturer) {
        dispatch(`vehicleModule${state.moduleKey}/submitManufacturer`, initialManufacturer, { root: true });
        dispatch('updateRanges');
        validInitManufacturer = true;
      }
    }

    if (!validInitManufacturer && manufacturers !== null && manufacturers.length === 1) {
      dispatch(`vehicleModule${state.moduleKey}/submitManufacturer`, manufacturers[0], { root: true });
      dispatch('updateRanges');
    }

    dispatch('policyModule/submitPolicyId', null, { root: true });
  },
  async updateRanges({
    state, commit, dispatch, rootState
  }) {
    if (rootState[`searchQueryModule${state.moduleKey}`].searchQuery.manufacturerIds === null || rootState[`searchQueryModule${state.moduleKey}`].searchQuery.manufacturerIds.length !== 1) {
      commit('setResults', new Results());
      commit('setFilteredResults', []);
      commit('setRanges', []);
      return;
    }

    await vehicleSearchService.fetchRanges(rootState[`searchQueryModule${state.moduleKey}`].searchQuery)
      .then((ranges: IRange[]) => {
        commit('setRanges', ranges);

        let validInitRange = false;

        if (state.vehicleStatusType === VehicleStatusType.Initial && rootState.initialSettingsModule.initialSettings.range) {
          const initialRange = ranges.find((x) => x.rangeId === rootState.initialSettingsModule.initialSettings.range);

          if (initialRange) {
            dispatch(`vehicleModule${state.moduleKey}/submitRange`, initialRange, { root: true });
            validInitRange = true;
          }
        }

        if (!validInitRange && ranges !== null && ranges.length === 1) {
          dispatch(`vehicleModule${state.moduleKey}/submitRange`, ranges[0], { root: true });
        }

        if (state.vehicleStatusType === VehicleStatusType.Initial) {
          dispatch('policyModule/submitPolicyId', null, { root: true });
        }
      });
  },
  async updateResults({
    state, commit, dispatch, rootState
  }): Promise<Results> {
    return new Promise((resolve) => {
      if (rootState[`searchQueryModule${state.moduleKey}`].searchQuery.rangeIds === null) {
        commit('setResults', new Results());
        resolve(state.results);
        return;
      }

      vehicleSearchService.fetchResults(rootState[`searchQueryModule${state.moduleKey}`].searchQuery)
        .then((result: Results) => {
          if (state.vehicleStatusType === VehicleStatusType.Initial && rootState.initialSettingsModule.initialSettings.range) {
            const urlQueries = window.location.href.split(/[?&]/);
            const modelSearchQuery: string | undefined = urlQueries.find((x) => x.startsWith('tlaModelId'));
            if (modelSearchQuery) {
              const modelId: number = parseInt(modelSearchQuery.split('=')[1], 10);
              const derivatives = result.items.filter((x) => x.modelId === modelId);

              if (derivatives && derivatives.length > 0) {
                result.items = derivatives;
                result.bodystyles = [...new Set(derivatives.flatMap((x) => x.bodystyles))];
              }
            }

            const derivativeSearchQuery: string | undefined = urlQueries.find((x) => x.startsWith('tlaDerivativeId'));
            if (derivativeSearchQuery) {
              const derivativeId: number = parseInt(derivativeSearchQuery.split('=')[1], 10);
              const derivative = result.items.find((x) => x.derivativeId === derivativeId);

              if (derivative) {
                result.items = [derivative];
                result.bodystyles = [derivative.bodystyles[0]];

                dispatch('vehicleModule/submitDerivative', derivative, { root: true });
              }
            }
          }

          commit('setResults', result);
          resolve(result);
        });
    });
  },
  updateFilteredResults({
    state, commit, rootState
  }) {
    const filteredResults = vehicleSearchService.fetchFilteredCustomisationResults(
      rootState[`searchQueryModule${state.moduleKey}`].searchQuery,
      state.results.items
    );

    commit('setFilteredResults', filteredResults);
  },
  submitResetResultsAndFilteredResults({ commit }) {
    commit('setResults', new Results());
    commit('setFilteredResults', []);
  },
  createNewModuleInstance({ }, options: VehicleModuleOptions) {
    const newStateInstance: IVehicleSearchState = new VehicleSearchState();
    newStateInstance.moduleKey = options.moduleKey;
    newStateInstance.vehicleStatusType = options.vehicleStatusType;

    this.registerModule(`vehicleSearchModule${options.moduleKey}`, {
      state: newStateInstance,
      getters,
      actions,
      mutations,
      namespaced: true
    });
  }
};

export const vehicleSearchModule: Module<IVehicleSearchState, IRootState> = {
  state, getters, mutations, actions, namespaced: true
};
